import type { ReactNode } from "react";
import type { FirstPlan, ProfileDetail } from "@amber/profiles/contracts";
import { profileCommands, relativeLower } from "@amber/profiles/ui";
import { AppLink, Button, Callout, Tag, useCommandMutation, Overline } from "@amber/ui";
import { AFTER_PLAN_CHANGE, ON_PLAN_CONFLICT } from "../queries";
import { PlatformCode } from "./PlatformCode";

function PlanGroup({
  title,
  count,
  hint,
  children,
}: {
  title: string;
  count: number;
  hint: string;
  children: ReactNode;
}) {
  return (
    <div className="mt-3">
      <h3 className="m-0 font-strong text-fg">
        {title} <span className="font-mono text-xs text-muted">{count}</span>
      </h3>
      <p className="m-0 mt-0.5 text-md text-muted">{hint}</p>
      {children}
    </div>
  );
}

function PreselectedList({ preselected, approved }: Pick<FirstPlan, "preselected" | "approved">) {
  if (preselected.length === 0 && approved.length > 0)
    return <p className="m-0 mt-1 text-md text-muted">None left: the sources found were approved one by one.</p>;
  if (preselected.length === 0)
    return (
      <p className="m-0 mt-1 text-md text-muted">
        No own source with strong evidence was found. Check the suggestions below.
      </p>
    );
  return (
    <ul className="m-0 mt-1.5 list-none p-0">
      {preselected.map((s) => (
        <li
          key={s.id}
          className="grid grid-cols-[30px_minmax(0,1fr)_minmax(0,1.3fr)] items-center gap-2 border-t border-divider py-2"
        >
          <PlatformCode code={s.platform_code} />
          <b className="truncate font-strong">{s.title}</b>
          <span className="text-md text-muted">
            {s.evidence_url ? (
              <a
                className="text-muted underline decoration-dotted"
                href={s.evidence_url}
                target="_blank"
                rel="noreferrer"
              >
                {s.why}
              </a>
            ) : (
              s.why
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ApprovedList({ approved }: { approved: FirstPlan["approved"] }) {
  return (
    <ul className="m-0 mt-1.5 list-none p-0">
      {approved.map((a) => (
        <li
          key={a.source_id}
          className="grid grid-cols-[30px_minmax(0,1fr)_minmax(0,1.3fr)] items-center gap-2 border-t border-divider py-2"
        >
          <PlatformCode code={a.platform_code} />
          <AppLink href={`/sources/${a.source_id}`} className="truncate font-strong">
            {a.title}
          </AppLink>
          <span className="text-md text-muted">{a.platform_label}</span>
        </li>
      ))}
    </ul>
  );
}

function CoveredList({ covered }: { covered: FirstPlan["covered"] }) {
  if (covered.length === 0) return null;
  return (
    <ul className="m-0 mt-1.5 list-none p-0">
      {covered.map((c) => (
        <li
          key={c.source_id}
          className="grid grid-cols-[30px_minmax(0,1fr)_minmax(0,1.3fr)_auto] items-center gap-2 border-t border-divider py-2"
        >
          <PlatformCode code={c.platform_code} />
          <AppLink href={`/sources/${c.source_id}`} className="truncate font-strong">
            {c.title}
          </AppLink>
          <span className="text-md">
            {c.why}
            <span className="block text-xs text-muted">Monitored through {c.monitored_for.join(", ")}</span>
          </span>
          <Tag>Already covered</Tag>
        </li>
      ))}
    </ul>
  );
}

function SuggestionList({ suggestions, onReview }: { suggestions: FirstPlan["suggestions"]; onReview: () => void }) {
  if (suggestions.length === 0) return null;
  return (
    <ul className="m-0 mt-1.5 list-none p-0">
      {suggestions.map((s) => (
        <li
          key={s.id}
          className="grid grid-cols-[30px_minmax(0,1fr)_minmax(0,1.3fr)_auto] items-center gap-2 border-t border-divider py-2"
        >
          <PlatformCode code={s.platform_code} />
          <b className="truncate font-strong">{s.title}</b>
          <span className="text-md">
            {s.why}
            {s.warning ? <span className="block text-xs text-waiting">{s.warning}</span> : null}
          </span>
          <Button size="sm" onClick={onReview}>
            Review
          </Button>
        </li>
      ))}
    </ul>
  );
}

export function FirstPlanCard({
  profile,
  plan,
  onStart,
  onReview,
  canStart,
}: {
  profile: ProfileDetail;
  plan: FirstPlan;
  onStart: () => void;
  onReview: () => void;
  canStart: string | null;
}) {
  const discover = useCommandMutation({
    run: (csrf) => profileCommands.discover(profile.id, csrf),
    invalidate: AFTER_PLAN_CHANGE,
    invalidateOnConflict: ON_PLAN_CONFLICT,
  });
  const name = profile.name.split(" ").at(-1) ?? profile.name;
  return (
    <section aria-label="Proposed first plan" className="mt-3 rounded-xl border border-divider px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <Overline className="m-0">
            Proposed first plan{" "}
            <span className="font-normal normal-case tracking-normal">
              drafted by source discovery
              {plan.discovery.at ? ` ${relativeLower(plan.discovery.at)}` : ""}
            </span>
          </Overline>
          <p className="m-0 mt-1.5 max-w-[640px] text-ui">
            Nothing is monitored until an Editor starts tracking. Starting approves the selected own sources, applies
            the plan and rechecks archived items for {name}.
          </p>
        </div>
        <Button variant="primary" size="sm" disabledReason={canStart} onClick={onStart}>
          Start tracking…
        </Button>
      </div>
      {plan.discovery.state === "failed" ? (
        <Callout
          tone="attention"
          className="mt-3"
          title="Source discovery failed"
          actions={
            <Button
              size="sm"
              onClick={() => {
                discover.mutate();
              }}
            >
              Retry
            </Button>
          }
        >
          {plan.discovery.detail ?? "Try again."}
        </Callout>
      ) : null}
      <PlanGroup
        title="Pre-selected · own sources"
        count={plan.preselected.length}
        hint="Strong evidence: linked from Wikidata or an official website, or a platform-verified account that matches the identity."
      >
        <PreselectedList preselected={plan.preselected} approved={plan.approved} />
      </PlanGroup>
      {plan.approved.length > 0 ? (
        <PlanGroup
          title="Approved · not in any plan yet"
          count={plan.approved.length}
          hint="Approved one by one. Start tracking adds them to the plan; nothing is scanned until then."
        >
          <ApprovedList approved={plan.approved} />
        </PlanGroup>
      ) : null}
      <PlanGroup
        title="Already covered · shared sources we monitor"
        count={plan.covered.length}
        hint={`No approval needed. Appearances there count for ${name}.`}
      >
        <CoveredList covered={plan.covered} />
      </PlanGroup>
      <PlanGroup
        title="Suggestions · need a decision"
        count={plan.suggestions.length}
        hint="Shared sources we don't monitor yet and own sources with weaker evidence. They stay open after tracking starts."
      >
        <SuggestionList suggestions={plan.suggestions} onReview={onReview} />
      </PlanGroup>
    </section>
  );
}
